'use client';
import React from 'react';
import { EdgeGateCTAOverlay, useEdgeRowsGate } from './EdgeRowsGate';

const quoteRowIsGated = (isGated: boolean, index: number, visibleRowCount: number): boolean =>
  isGated && index >= visibleRowCount;

const quoteRowClass = (index: number, blurred?: boolean, extra?: string): string =>
  [
    'border-b border-gray-100',
    index % 2 === 1 ? 'bg-gray-50' : '',
    blurred ? 'blur-sm opacity-60 select-none' : '',
    extra ?? '',
  ]
    .filter(Boolean)
    .join(' ');

export const useQuoteGatedRows = (rowCount: number, visibleRows = 2) => {
  const { isGated, visibleRowCount } = useEdgeRowsGate(rowCount, visibleRows);
  return {
    isGated,
    rowBlurred: (index: number) => quoteRowIsGated(isGated, index, visibleRowCount),
    visibleRowCount,
  };
};

export const QuoteDataTable: React.FC<{
  children: React.ReactNode;
  className?: string;
  overlay?: React.ReactNode;
  tableClassName?: string;
}> = ({ children, className, overlay, tableClassName }) => {
  const table = (
    <div className={`overflow-x-auto rounded border border-[#e9ecef]${className ? ` ${className}` : ''}`}>
      <table className={`w-full min-w-max text-sm${tableClassName ? ` ${tableClassName}` : ''}`}>{children}</table>
    </div>
  );
  if (!overlay) return table;
  return (
    <div className="relative">
      {table}
      {overlay}
    </div>
  );
};

export const QuoteGatedTable: React.FC<{
  children: React.ReactNode;
  className?: string;
  gateType: string;
  rowCount: number;
  visibleRows?: number;
}> = ({ children, className, gateType, rowCount, visibleRows = 2 }) => {
  const { isGated, visibleRowCount } = useEdgeRowsGate(rowCount, visibleRows);
  const hasOverlay = isGated && rowCount > visibleRowCount;

  return (
    <QuoteDataTable className={className} overlay={hasOverlay ? <EdgeGateCTAOverlay type={gateType} /> : undefined}>
      {children}
    </QuoteDataTable>
  );
};

export const QuoteThead: React.FC<{ children: React.ReactNode }> = ({ children }) => <thead>{children}</thead>;

export const QuoteHeaderRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <tr className="border-b border-gray-100 bg-gray-50">{children}</tr>
);

export const QuoteTh: React.FC<{
  align?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLTableCellElement>;
}> = ({ align = 'left', children, className, onClick }) => (
  <th
    className={`px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}${className ? ` ${className}` : ''}`}
    onClick={onClick}
  >
    {children}
  </th>
);

export const QuoteSectionRow: React.FC<{ children: React.ReactNode; colSpan: number }> = ({ children, colSpan }) => (
  <tr className="border-b border-gray-200 bg-bzblue-300">
    <td colSpan={colSpan} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-bzblue-1000">
      {children}
    </td>
  </tr>
);

export const QuoteTr: React.FC<{
  blurred?: boolean;
  children: React.ReactNode;
  className?: string;
  index: number;
}> = ({ blurred, children, className, index }) => (
  <tr className={quoteRowClass(index, blurred, className)} aria-hidden={blurred || undefined}>
    {children}
  </tr>
);

export const QuoteTd: React.FC<{
  align?: 'left' | 'right';
  bold?: boolean;
  children: React.ReactNode;
  className?: string;
  muted?: boolean;
}> = ({ align = 'left', bold, children, className, muted }) => (
  <td
    className={`px-3 py-2 whitespace-nowrap text-sm ${align === 'right' ? 'text-right' : 'text-left'} ${
      bold ? 'font-semibold text-gray-900' : muted ? 'text-gray-400' : 'text-gray-700'
    }${className ? ` ${className}` : ''}`}
  >
    {children}
  </td>
);

export default QuoteDataTable;
